import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { BackButton } from "components/BackButton";
import { Layout } from "components/Layout";
import { showAlert } from "redux/actions/setAppActions";
import { useDispatch, useSelector } from "react-redux";
import { InvitePsychologistModal } from "components/modals/InvitePsychologistModal";
import { InvitedPsychologistsList } from "components/schools/InvitedPsychologistsList";
import { canEditSchool } from "helpers/schoolPermissions";

/**
 * @api {get} /schools/create создание школы
 * @apiGroup schools and classes
 * @apiName создание школы
 *
 * @apiDescription <p>Страница создания школы</p>
 * <p>Компонент <code>SchoolForm.js</code> <a href="../client/src/pages/schools/SchoolForm.js">ссылка на файл</a></p>
 */

/**
 * @api {get} /schools/:id редактирования школы
 * @apiGroup schools and classes
 * @apiName редактирования школы
 *
 * @apiParam {String} id ID школы
 *
 * @apiDescription <p>Страница редактирования школы</p>
 * <p>Компонент <code>SchoolForm.js</code> <a href="../client/src/pages/schools/SchoolForm.js">ссылка на файл</a></p>
 */

export const SchoolForm = () => {
    const { t } = useTranslation("schools");
    const history = useHistory();
    const dispatch = useDispatch();
    const schoolId = useParams().id;
    const [school, setSchool] = useState(null);
    const [name, setName] = useState("");
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const currentUserId = useSelector((state) => state.auth.user?._id);

    useEffect(() => {
        if (schoolId) {
            (async () => {
                const { data } = await axios.get(`schools/${schoolId}`);
                setSchool(data);
                setName(data.name);
            })();
        }
    }, [schoolId, refreshKey]);

    const changeHandler = (event) => {
        setName(event.target.value);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (school) {
            await axios.post(`schools/${school._id}`, { name });
        } else {
            try {
                await axios.post("schools/create", { name });
            } catch (e) {
                if(e.response.status === 400){
                    dispatch(showAlert({type: 'error', text: e.response.data.message}));
                }
            }
        }

        history.goBack();
    };

    const handleInviteSuccess = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Проверяем, является ли текущий пользователь владельцем школы
    const isOwner = school && currentUserId ? canEditSchool(school, currentUserId) : false;

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title">{t("schools")}</p>
                </header>

                <div className="school-page__wrapper">
                    <h3>{school ? t("change_school") : t("add_school")}</h3>
                    <div className="input-field">
                        <input
                            id="name"
                            type="text"
                            name="name"
                            placeholder={t("name")}
                            onChange={changeHandler}
                            value={name}
                        />
                        <label htmlFor="name">{t("name")}</label>
                    </div>

                    {/* Секция приглашения психологов - только при редактировании школы */}
                    {school && (
                        <>
                            <InvitedPsychologistsList 
                                schoolId={school._id} 
                                isOwner={isOwner}
                                key={refreshKey}
                            />
                            
                            {isOwner && (
                                <button 
                                    onClick={() => setShowInviteModal(true)} 
                                    className="main-btn"
                                    style={{ marginTop: 16, marginBottom: 16, background: "#4caf50" }}
                                >
                                    {t("invite_psychologist_btn")}
                                </button>
                            )}
                        </>
                    )}
                </div>

                <button onClick={submit} disabled={!name} className="main-btn">
                    {school ? t("change_school") : t("add_school")}
                </button>

                {/* Модалка приглашения психолога */}
                {showInviteModal && (
                    <InvitePsychologistModal
                        schoolId={school._id}
                        onClose={() => setShowInviteModal(false)}
                        onSuccess={handleInviteSuccess}
                    />
                )}
            </div>
        </Layout>
    );
};
